import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  alpha,
  Skeleton,
  Alert,
} from "@mui/material";
import ParkIcon from "@mui/icons-material/Park";
import NaturePeopleIcon from "@mui/icons-material/NaturePeople";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck"; // ✅ Added for Eligibility
import FolderSharedIcon from "@mui/icons-material/FolderShared"; // ✅ Added for Documents
import { useUser } from "../../Context/UserContext";
import { GetApi } from "../../Services/api/ApiCall";
import "../../i8n";
import { useTranslation } from "react-i18next";
import i18n from "../../i8n";

const colors = {
  darkGreen: "#1B5E20",
  mediumGreen: "#2E7D32",
  lightGreen: "#4CAF50",
  paleGreen: "#E8F5E9",
  darkOrange: "#E65100",
  mediumOrange: "#F57C00",
  lightOrange: "#FF9800",
  paleOrange: "#FFF3E0",
  darkBlue: "#1565C0", // ✅ Added for Documents
  mediumBlue: "#1976D2", // ✅ Added for Documents
  paleBlue: "#E3F2FD", // ✅ Added for Documents
  white: "#FFFFFF",
  darkText: "#1a1a1a",
  subText: "#4a4a4a",
};

export default function Scheme() {
  const { t } = useTranslation();
  const { authData } = useUser();
  const token = authData?.token;

  const [schemesData, setSchemesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSchemes = async () => {
      setLoading(true);
      try {
        const data = await GetApi('/dynamic/dashboard-schemes', token);
        if (data) {
          setSchemesData(data);
        }
      } catch (err) {
        console.error("Failed to fetch schemes:", err);
        setError("Failed to load schemes. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchSchemes();
  }, [token]);

  const SkeletonCard = () => (
    <Grid size={{ xs: 12, md: 6 }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          height: "100%",
          border: "2px solid",
          borderColor: alpha(colors.darkGreen, 0.1),
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Skeleton variant="rectangular" height={220} width="100%" animation="wave" />
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="80%" />
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        bgcolor: "#FAFFFE",
        minHeight: "80vh",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Chip
            icon={<NaturePeopleIcon />}
            label={t("scheme.chip")}
            sx={{
              bgcolor: colors.paleGreen,
              color: colors.darkGreen,
              fontWeight: 700,
              fontSize: "0.8rem",
              py: 2.5,
              px: 1,
              mb: 2,
              "& .MuiChip-icon": { color: colors.darkGreen },
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: colors.darkGreen,
              fontSize: { xs: "1.8rem", md: "2.5rem" },
              mb: 1.5,
            }}
          >
            {t("scheme.title")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.subText,
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            {t("scheme.subtitle")}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
        )}

        <Grid container spacing={4}>
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : schemesData.length > 0 ? (
            schemesData.map((scheme, index) => {
              const currentLang = i18n.language;
              
              // ✅ Select correct language arrays based on i18n
              const aboutText = currentLang === 'ta' ? scheme.about_scheme_ta : scheme.about_scheme_en;
              const speciesList = currentLang === 'ta' ? scheme.preferred_species_ta : scheme.preferred_species_en;
              const eligibilityList = currentLang === 'ta' ? scheme.scheme_eligibility_ta : scheme.scheme_eligibility_en;
              const documentsList = currentLang === 'ta' ? scheme.scheme_documents_ta : scheme.scheme_documents_en;

              return (
                <Grid size={{ xs: 12, md: 6 }} key={scheme.id || index}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      height: "100%",
                      border: "2px solid",
                      borderColor: alpha(colors.darkGreen, 0.1),
                      transition: "all 0.35s ease",
                      cursor: "default",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": {
                        borderColor: colors.darkGreen,
                        transform: "translateY(-6px)",
                        boxShadow: `0 16px 40px ${alpha(colors.darkGreen, 0.15)}`,
                      },
                    }}
                  >
                    {/* Banner Image Area */}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: 220,
                        overflow: "hidden",
                        background: `linear-gradient(135deg, ${colors.darkGreen}, ${colors.lightGreen})`,
                      }}
                    >
                      <Box
                        component="img"
                        src={scheme.image_url}
                        alt="Scheme"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.5s ease",
                          zIndex: 1,
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <Box
                        sx={{
                          display: "none",
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          alignItems: "center",
                          justifyContent: "center",
                          background: `linear-gradient(135deg, ${colors.darkGreen}, ${colors.lightGreen})`,
                          zIndex: 0,
                        }}
                      >
                        <ParkIcon sx={{ fontSize: 64, color: alpha(colors.white, 0.6) }} />
                      </Box>

                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "60%",
                          background: `linear-gradient(to top, ${alpha(colors.darkGreen, 0.7)}, transparent)`,
                        }}
                      />

                      <Box sx={{ position: "absolute", bottom: 16, left: 20, right: 20 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: colors.white,
                            textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                            fontSize: { xs: "1.1rem", md: "1.3rem" },
                          }}
                        >
                          {t("scheme.title")} {index + 1}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        height: 4,
                        background: `linear-gradient(90deg, ${colors.darkGreen}, ${colors.lightGreen}, ${colors.mediumOrange})`,
                      }}
                    />

                    <CardContent
                      sx={{
                        p: { xs: 3, md: 4 },
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* About Section */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <DescriptionIcon sx={{ fontSize: 20, color: colors.mediumGreen }} />
                          <Typography variant="subtitle2" sx={{ color: colors.darkGreen, fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: 0.8 }}>
                            {t("aboutLabel")}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: colors.subText, lineHeight: 1.8, fontSize: "0.9rem" }}>
                          {aboutText}
                        </Typography>
                      </Box>

                      {/* Preferred Species Section */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <LocalFloristIcon sx={{ fontSize: 20, color: colors.mediumGreen }} />
                          <Typography variant="subtitle2" sx={{ color: colors.darkGreen, fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: 0.8 }}>
                            {t("scheme.preferredSpecies")}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {speciesList && speciesList.length > 0 ? (
                            speciesList.map((speciesName, idx) => (
                              <Chip
                                key={idx}
                                label={speciesName}
                                variant="outlined"
                                size="small"
                                sx={{
                                  borderColor: colors.lightGreen,
                                  color: colors.darkGreen,
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  transition: "all 0.2s ease",
                                  "&:hover": { bgcolor: colors.paleGreen, borderColor: colors.mediumGreen },
                                }}
                                icon={<LocalFloristIcon sx={{ fontSize: 14, color: colors.mediumGreen, ml: 0.5 }} />}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.subText, fontStyle: "italic" }}>
                              {currentLang === 'ta' ? "இனங்கள் எதுவும் இல்லை" : "No species listed"}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* ✅ Scheme Eligibility Section */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <FactCheckIcon sx={{ fontSize: 20, color: colors.darkOrange }} />
                          <Typography variant="subtitle2" sx={{ color: colors.darkOrange, fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: 0.8 }}>
                            {t("scheme.eligibility", currentLang === 'ta' ? "தகுதி" : "Eligibility")}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {eligibilityList && eligibilityList.length > 0 ? (
                            eligibilityList.map((item, idx) => (
                              <Chip
                                key={idx}
                                label={item}
                                variant="outlined"
                                size="small"
                                sx={{
                                  borderColor: colors.lightOrange,
                                  color: colors.darkOrange,
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  transition: "all 0.2s ease",
                                  "&:hover": { bgcolor: colors.paleOrange, borderColor: colors.mediumOrange },
                                }}
                                icon={<FactCheckIcon sx={{ fontSize: 14, color: colors.mediumOrange, ml: 0.5 }} />}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.subText, fontStyle: "italic" }}>
                              {currentLang === 'ta' ? "தகுதி விவரங்கள் இல்லை" : "No eligibility details"}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* ✅ Required Documents Section */}
                      <Box sx={{ mt: "auto" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <FolderSharedIcon sx={{ fontSize: 20, color: colors.darkBlue }} />
                          <Typography variant="subtitle2" sx={{ color: colors.darkBlue, fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: 0.8 }}>
                            {t("scheme.documents", currentLang === 'ta' ? "தேவையான ஆவணங்கள்" : "Required Documents")}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {documentsList && documentsList.length > 0 ? (
                            documentsList.map((item, idx) => (
                              <Chip
                                key={idx}
                                label={item}
                                variant="outlined"
                                size="small"
                                sx={{
                                  borderColor: colors.mediumBlue,
                                  color: colors.darkBlue,
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  transition: "all 0.2s ease",
                                  "&:hover": { bgcolor: colors.paleBlue, borderColor: colors.darkBlue },
                                }}
                                icon={<FolderSharedIcon sx={{ fontSize: 14, color: colors.darkBlue, ml: 0.5 }} />}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.subText, fontStyle: "italic" }}>
                              {currentLang === 'ta' ? "ஆவணங்கள் எதுவும் இல்லை" : "No documents listed"}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          ) : (
             <Grid item xs={12}>
               <Typography variant="h6" align="center" color="textSecondary">
                {t("emptySchemeNote")}
                 
               </Typography>
            </Grid>
          )}
        </Grid>

        {/* Bottom Note */}
        <Box
          sx={{
            mt: 8,
            textAlign: "center",
            p: 4,
            borderRadius: 3,
            bgcolor: alpha(colors.paleGreen, 0.6),
            border: `1px solid ${alpha(colors.lightGreen, 0.3)}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: colors.subText,
              fontSize: "0.9rem",
              lineHeight: 1.7,
            }}
          >
            <strong style={{ color: colors.darkGreen }}>
              {i18n.language === "ta" ? "குறிப்பு:" : "Note:"}
            </strong>{" "}
            {t("schemesNote")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}